import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {TablePageSelector} from '../../../../components/table/tablePagination/tablePageSelector';
import {
  LiveDashboardContentInboxWorkloadModel,
  LiveDashboardContentWorkloadByInboxModel,
} from '../../../../models/api/liveDashboardContentModel';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {ReportSection} from '../../reportElements/reportSection/reportSection';
import {useAnalyticsTable} from '../../table/hooks/useAnalyticsTable';
import {TableContent} from '../../table/tableContent';
import {LIVE_DASHBOARD_HELP_CENTER_URL} from '../constants';
import {
  liveDashboardWorkloadByInboxColumns,
  liveDashboardWorkloadByInboxColumnsWithWaiting,
} from './liveDashboardWorkloadByInboxColumns';

/*
 * Props.
 */

interface LiveDashboardWorkloadByInboxSectionProps {
  workloadByInbox: LiveDashboardContentWorkloadByInboxModel | undefined;
  hasWaitingMetrics: boolean;
}

/*
 * Styles.
 */

const SyledTableWrapperDiv = styled.div`
  & tbody {
    font-size: ${fontSizes.medium};
  }
`;

/*
 * Component.
 */

export const LiveDashboardWorkloadByInboxSection: FC<LiveDashboardWorkloadByInboxSectionProps> = ({
  workloadByInbox,
  hasWaitingMetrics,
}) => {
  const rows = useMemo(() => [...(workloadByInbox?.inboxWorkloads ?? [])], [workloadByInbox]);
  const columns = hasWaitingMetrics
    ? liveDashboardWorkloadByInboxColumnsWithWaiting
    : liveDashboardWorkloadByInboxColumns;

  const {tableInstance} = useAnalyticsTable({
    columns,
    data: rows,
    hiddenColumnIds: [],
    disableGlobalFilter: true,
    disableSelection: true,
    autoResetPage: false,
    initialState: {
      pageIndex: 0,
      pageSize: 5,
      sortBy: [
        {
          id: 'open',
          desc: true,
        },
      ],
    },
  });

  const renderPageSelector = () => <TablePageSelector instance={tableInstance} />;

  return (
    <ReportSection
      isDisabled={false}
      reportSectionId={ReportElementIdsEnum.LIVE_DASHBOARD_WORKLOAD_BY_INBOX_TABLE}
      title={sectionTooltip.title}
      sectionTooltip={sectionTooltip}
      customContentRenderer={() => renderPageSelector()}
    >
      <SyledTableWrapperDiv>
        <TableContent<LiveDashboardContentInboxWorkloadModel>
          tableInstance={tableInstance}
          isLoading={!isDefined(workloadByInbox)}
          disableTablePagination
          shouldHideBottomBorder
        />
      </SyledTableWrapperDiv>
    </ReportSection>
  );
};

/*
 * Constants.
 */

const sectionTooltip = {
  title: <Trans id="live-dashboard-workload-by-inbox">Workload by inbox</Trans>,
  tooltipContent: (
    <Trans id="analytics-live-workload-by-inbox-tooltip-description">
      Compare workload and oldest unreplied time by inboxes to identify risks.
    </Trans>
  ),
  learnMoreUrl: `${LIVE_DASHBOARD_HELP_CENTER_URL}#workload_by_inbox_table`,
};
